import React, {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { isEmpty } from "lodash";
import { Grid } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import CentralLoader from "./CentralLoader";
import Header from "../containers/Header";
import { getSessionStorage, getValue, setSessionStorage } from "../../utils";
import RoutesList from "../../routes";
import { UserModuleResponse } from "../../models/Api/Modules";
import { LogoutResponse } from "../../models/Api/Logout";
import UnAuthorizedPage from "../../components/errorPages/401";
import { ExchangeCodePayload, ExchangedToken } from "../../models/Sso";
import { forbiddenStore } from "../../utils/forbiddenStore";
import InvalidRequestPage from "../../components/errorPages/InvalidRequest";
import SessionTimeout from "../common/SessionTimeoutModal";
import { AppRoutes } from "../../constants/ApplicationUrl";
import Logout from "../../components/Logout";

export interface PropsFromState {
  exchangedTokenState: ExchangedToken | null;
  refreshedTokenState: any;
  moduleData: UserModuleResponse;
  exchangedTokenLoading: boolean;
  logoutResp: LogoutResponse;
}
export interface PropsFromDispatch {
  getExchangedToken: (payload: ExchangeCodePayload) => void;
  getRefreshedToken: (payload: any) => void;
  logoutOfApp: () => void;
  getUserModules: () => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const AppContent: React.FC<AllProps> = ({
  getExchangedToken,
  getRefreshedToken,
  getUserModules,
  exchangedTokenState,
  refreshedTokenState,
  moduleData,
  exchangedTokenLoading,
  logoutResp,
  logoutOfApp,
}) => {
  const [isSAMLTokenAvailable, setIsSAMLTokenAvailable] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);

  const isForbidden = useSyncExternalStore(
    forbiddenStore.subscribe,
    forbiddenStore.getSnapshot
  );

  const navigate = useNavigate();
  const hasRequestedToken = useRef(false);
  const location = useLocation();
  const isLogoutRoute = location.pathname === AppRoutes.LOGOUT;
  const isLogoutSuccess =
    getValue(logoutResp, "response.message", "") === "Logged out.";

  const params = new URLSearchParams(location.search);
  const ssoError = params.get("error");
  const hasSamlCode = params.has("samlCode");
  const showLoader = isInitializing || (hasSamlCode && exchangedTokenLoading && !isSAMLTokenAvailable);

  const refreshTimerRef = useRef<number | null>(null);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (ssoError) {
      sessionStorage.clear();
    }
  }, [ssoError]);

  useEffect(() => {
    if (isLogoutSuccess) {
      clearRefreshTimer();
      navigate(AppRoutes.LOGOUT, { replace: true });
      sessionStorage.clear();
    }
  }, [isLogoutSuccess]);

  const scheduleRefresh = (expiresInSeconds: number, refreshToken: string) => {
    clearRefreshTimer();
    const refreshBefore = Math.min(300, Math.floor(expiresInSeconds / 2));
    const ms = (expiresInSeconds - refreshBefore) * 1000;
    if (ms <= 0) {
      getRefreshedToken({ refreshToken });
      return;
    }
    refreshTimerRef.current = window.setTimeout(() => {
      getRefreshedToken({ refreshToken });
    }, ms) as unknown as number;
  };

  useEffect(() => {
    return () => {
      clearRefreshTimer();
    };
  }, []);

  useEffect(() => {
    const accessToken = getValue(exchangedTokenState, "accessToken", "");
    const existingToken = getSessionStorage("samlToken");
    const shouldSeedFromExchangeToken =
      Boolean(accessToken) && (!existingToken || existingToken === accessToken);

    const expiresInRaw = getValue(exchangedTokenState, "expiresIn");
    const expiresIn = Number(expiresInRaw) || 0;
    const refreshToken = getValue(exchangedTokenState, "refreshToken", "");

    if (shouldSeedFromExchangeToken && expiresIn > 0 && refreshToken) {
      scheduleRefresh(expiresIn, refreshToken);
    }
    
    if (shouldSeedFromExchangeToken) {
      setSessionStorage("samlToken", accessToken);
      if (refreshToken) {
        setSessionStorage("refreshToken", refreshToken);
      }

      const user = getValue(exchangedTokenState, "user", null);
      if (user) {
        setSessionStorage("loggedInUser", user);
      }
      setIsSAMLTokenAvailable(true);
      setIsInitializing(false);
      if (hasSamlCode) {
        navigate("/", {
          replace: true,
        });
      }
      return;
    }
    if (!isEmpty(existingToken)) {
      setIsSAMLTokenAvailable(true);
      setIsInitializing(false);
      return;
    }
    if (!hasSamlCode) {
      setIsInitializing(false);
    }
  
  }, [
    exchangedTokenState,
    hasSamlCode,
    navigate,
  ]);

  useEffect(() => {
    const accessToken =
      getValue(exchangedTokenState, "accessToken", "") ||
      getSessionStorage("samlToken");

    if (!accessToken) {
      return;
    }

    getUserModules();
  }, [exchangedTokenState, getUserModules, isSAMLTokenAvailable]);

    useEffect(() => {
    const newToken = getValue(refreshedTokenState, "accessToken", "")

    if (newToken) {
      setSessionStorage("samlToken", newToken);
      const expiresIn = Number(getValue(refreshedTokenState, "expiresIn", 0));

      const refreshedRefreshToken =  getValue(refreshedTokenState, "refreshToken", "");

      if (refreshedRefreshToken) {
        setSessionStorage("refreshToken", refreshedRefreshToken);
      }

      const refreshTokenToUse =
        refreshedRefreshToken || getSessionStorage("refreshToken") || "";

      if (expiresIn > 0 && refreshTokenToUse) {
        scheduleRefresh(expiresIn, refreshTokenToUse);
      }
    }
  }, [refreshedTokenState]);

  useEffect(() => {
    if (!hasSamlCode) {
      return;
    }
    if (!isEmpty(getSessionStorage("samlToken"))) {
      setIsSAMLTokenAvailable(true);
      setIsInitializing(false);
      if (hasSamlCode) {
        navigate("/", {
          replace: true,
        });
      }
      return;
    }

    const dataSaml = params.get("samlCode");
    if (
      !hasRequestedToken.current &&
      dataSaml &&
      !isEmpty(dataSaml) &&
      !getValue(exchangedTokenState, "accessToken")
    ) {
      hasRequestedToken.current = true;
      getExchangedToken({
        code: dataSaml,
      });
    }
  }, []);

  if (showLoader) {
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <CentralLoader />
        </Grid>
      </Grid>
    );
  }

  if (isLogoutSuccess) {
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Logout />
        </Grid>
      </Grid>
    );
  }

  if (isForbidden) {
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <UnAuthorizedPage />
        </Grid>
      </Grid>
    );
  }

  if (ssoError) {
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Grid container direction="column">
            <Grid>
              <InvalidRequestPage />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  if (!isSAMLTokenAvailable) {
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Grid container direction="column">
            <Grid>
              <UnAuthorizedPage />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Header />
        {!isLogoutRoute && <SessionTimeout logoutHppApp={logoutOfApp} />}

        <RoutesList moduleData={moduleData} />

        <CentralLoader />
      </Grid>
    </Grid>
  );
};

export default AppContent;
